// src/features/services/sales/components/SkuCatalogPanel.tsx
// ─────────────────────────────────────────────────────────────────────────────
// SKU Catalog Management — create, view, toggle SKU definitions.
// PAC Glass design system. Zero-prop — data via useSkuCatalog hook.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSkuCatalog } from '../hooks/useSkuCatalog';
import type { UpsertSkuPayload, SkuDefinition } from '../api/skuCatalogApi';

// ── Style tokens (matches CreateSalesOrderPanel) ─────────────────────────
const INPUT =
  'mt-1 w-full bg-black border border-neutral-700 rounded px-3 py-2 text-sm text-neutral-200 placeholder-neutral-600 outline-none focus:ring-2 focus:ring-emerald-500';
const SELECT = INPUT;
const LABEL = 'block text-xs text-neutral-400 uppercase tracking-wider';

const CATEGORIES = ['iot', 'pacdaemon', 'retail', 'accessory', 'general'] as const;

// ── Empty form state ─────────────────────────────────────────────────────
const SKU_TYPES = ['assembly', 'component'] as const;

const EMPTY_FORM: UpsertSkuPayload = {
  sku: '',
  label: '',
  description: '',
  category: 'iot',
  sku_type: 'assembly',
  tier: 0,
  parent_sku: null,
  version: '1.0.0',
  publish_period_ms: 10000,
  sensor_interval_ms: 10000,
  sensor_enabled: true,
  config_poll_ms: 120000,
  log_flush_ms: 30000,
  wifi_enabled: true,
};

// ── Component ────────────────────────────────────────────────────────────

export default function SkuCatalogPanel() {
  const qc = useQueryClient();
  const { data, isLoading, isError, error, upsert, deactivate, reactivate } = useSkuCatalog();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<UpsertSkuPayload>({ ...EMPTY_FORM });
  const [editingSku, setEditingSku] = useState<string | null>(null);

  const items: SkuDefinition[] = data?.items ?? [];

  const set = useCallback(
    <K extends keyof UpsertSkuPayload>(key: K, val: UpsertSkuPayload[K]) =>
      setForm((prev) => ({ ...prev, [key]: val })),
    [],
  );

  const canSubmit =
    form.sku?.trim() &&
    form.label?.trim() &&
    !upsert.isPending;

  // ── Open form for new SKU ──────────────────────────────────────────────
  const openNew = () => {
    setForm({ ...EMPTY_FORM });
    setEditingSku(null);
    setShowForm(true);
  };

  // ── Open form pre-filled for editing ───────────────────────────────────
  const openEdit = (s: SkuDefinition) => {
    setForm({
      sku: s.sku,
      label: s.label,
      description: s.description,
      category: s.category,
      sku_type: s.sku_type ?? 'assembly',
      tier: s.tier ?? 0,
      parent_sku: s.parent_sku ?? null,
      version: s.version,
      publish_period_ms: s.publish_period_ms ?? undefined,
      sensor_interval_ms: s.sensor_interval_ms ?? undefined,
      sensor_enabled: s.sensor_enabled ?? undefined,
      config_poll_ms: s.config_poll_ms ?? undefined,
      log_flush_ms: s.log_flush_ms ?? undefined,
      wifi_enabled: s.wifi_enabled ?? undefined,
    });
    setEditingSku(s.sku);
    setShowForm(true);
  };

  // ── Submit ─────────────────────────────────────────────────────────────
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      await upsert.mutateAsync(form);
    } catch {
      // Error state handled by upsert.isError in the JSX
      return;
    }
    // Also invalidate the sales-order SKU dropdown cache
    qc.invalidateQueries({ queryKey: ['sales', 'skus'] });
    setShowForm(false);
    setEditingSku(null);
    setForm({ ...EMPTY_FORM });
  };

  // ── Toggle active / inactive ───────────────────────────────────────────
  const toggleActive = async (s: SkuDefinition) => {
    try {
      if (s.active) {
        await deactivate.mutateAsync(s.sku);
      } else {
        await reactivate.mutateAsync(s.sku);
      }
    } catch {
      return;
    }
    qc.invalidateQueries({ queryKey: ['sales', 'skus'] });
  };

  // ── Derived ─────────────────────────────────────────────────────────────
  const listReady = !isLoading && !isError;

  return (
    <div className="pac-glass rounded-lg p-5 space-y-4">
      {/* ── Header ───────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-neutral-100 tracking-tight">
          SKU Catalog
        </h2>
        <button
          type="button"
          onClick={showForm ? () => setShowForm(false) : openNew}
          className="pac-glass-btn px-3 py-1.5 rounded text-xs font-medium border border-neutral-700
                     text-neutral-300 hover:border-emerald-500 hover:text-emerald-400 transition-colors"
        >
          {showForm ? 'Cancel' : '+ New SKU'}
        </button>
      </div>

      {/* ── Create / Edit Form ───────────────────────────────────────── */}
      {showForm && (
        <form onSubmit={onSubmit} className="pac-glass-subtle rounded-lg p-4 space-y-4 border border-neutral-800">
          <p className="text-xs text-neutral-500 uppercase tracking-wider font-medium">
            {editingSku ? `Edit: ${editingSku}` : 'New SKU Definition'}
          </p>

          {/* Row 1: SKU code + Label */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className={LABEL}>
              SKU Code
              <input
                value={form.sku ?? ''}
                onChange={(e) => set('sku', e.target.value)}
                placeholder="pacdaemon-iot-telemetry"
                maxLength={128}
                disabled={!!editingSku}
                className={INPUT + (editingSku ? ' opacity-50 cursor-not-allowed' : '')}
              />
            </label>
            <label className={LABEL}>
              Label
              <input
                value={form.label ?? ''}
                onChange={(e) => set('label', e.target.value)}
                placeholder="PACDaemon IoT Telemetry Sensor"
                className={INPUT}
              />
            </label>
          </div>

          {/* Row 2: Category + SKU Type + Version + Description */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <label className={LABEL}>
              Category
              <select
                value={form.category ?? 'iot'}
                onChange={(e) => set('category', e.target.value)}
                className={SELECT}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c} className="bg-neutral-900">{c}</option>
                ))}
              </select>
            </label>
            <label className={LABEL}>
              Type
              <select
                value={form.sku_type ?? 'assembly'}
                onChange={(e) => set('sku_type', e.target.value)}
                className={SELECT}
              >
                {SKU_TYPES.map((t) => (
                  <option key={t} value={t} className="bg-neutral-900">{t}</option>
                ))}
              </select>
            </label>
            <label className={LABEL}>
              Version
              <input
                value={form.version ?? ''}
                onChange={(e) => set('version', e.target.value)}
                placeholder="1.0.0"
                className={INPUT}
              />
            </label>
            <label className={LABEL}>
              Description
              <input
                value={form.description ?? ''}
                onChange={(e) => set('description', e.target.value)}
                placeholder="Short description"
                className={INPUT}
              />
            </label>
          </div>

          {/* Row 3: Tier + Parent SKU */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <label className={LABEL}>
              Tier
              <input
                type="number"
                min={0}
                max={10}
                value={form.tier ?? 0}
                onChange={(e) => set('tier', Math.max(0, Math.min(10, Number(e.target.value) || 0)))}
                className={INPUT}
              />
              <span className="text-[9px] text-neutral-600 mt-0.5 block">
                0 = assembly (top-level), 1+ = component depth
              </span>
            </label>
            <label className={LABEL + ' sm:col-span-2'}>
              Parent Assembly
              <select
                value={form.parent_sku ?? ''}
                onChange={(e) => set('parent_sku', e.target.value || null)}
                className={SELECT}
              >
                <option value="" className="bg-neutral-900">
                  — none (top-level) —
                </option>
                {items.filter((s) => (s.sku_type ?? 'assembly') === 'assembly' && s.active && s.sku !== form.sku).map((s) => (
                  <option key={s.sku} value={s.sku} className="bg-neutral-900">
                    {s.label || s.sku}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {/* Row 4: IoT Defaults */}
          <div>
            <p className="text-[10px] text-neutral-500 uppercase tracking-wider mb-2">
              IoT Device Defaults
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <label className={LABEL}>
                Publish (ms)
                <input
                  type="number"
                  min={1000}
                  value={form.publish_period_ms ?? ''}
                  onChange={(e) => set('publish_period_ms', Number(e.target.value) || undefined)}
                  placeholder="10000"
                  className={INPUT}
                />
              </label>
              <label className={LABEL}>
                Sensor Read (ms)
                <input
                  type="number"
                  min={1000}
                  value={form.sensor_interval_ms ?? ''}
                  onChange={(e) => set('sensor_interval_ms', Number(e.target.value) || undefined)}
                  placeholder="10000"
                  className={INPUT}
                />
              </label>
              <label className={LABEL}>
                Config Poll (ms)
                <input
                  type="number"
                  min={5000}
                  value={form.config_poll_ms ?? ''}
                  onChange={(e) => set('config_poll_ms', Number(e.target.value) || undefined)}
                  placeholder="120000"
                  className={INPUT}
                />
              </label>
              <label className={LABEL}>
                Log Flush (ms)
                <input
                  type="number"
                  min={1000}
                  value={form.log_flush_ms ?? ''}
                  onChange={(e) => set('log_flush_ms', Number(e.target.value) || undefined)}
                  placeholder="30000"
                  className={INPUT}
                />
              </label>
              <label className={LABEL + ' flex items-end gap-2'}>
                <span className="mb-2">Sensor</span>
                <input
                  type="checkbox"
                  checked={form.sensor_enabled ?? true}
                  onChange={(e) => set('sensor_enabled', e.target.checked)}
                  className="accent-emerald-500 w-4 h-4 mb-2"
                />
              </label>
              <label className={LABEL + ' flex items-end gap-2'}>
                <span className="mb-2">WiFi</span>
                <input
                  type="checkbox"
                  checked={form.wifi_enabled ?? true}
                  onChange={(e) => set('wifi_enabled', e.target.checked)}
                  className="accent-emerald-500 w-4 h-4 mb-2"
                />
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-1">
            <button
              type="submit"
              disabled={!canSubmit}
              className="pac-glass-btn px-5 py-2 rounded text-sm font-medium border border-neutral-700
                         text-neutral-200 hover:border-emerald-500 hover:text-emerald-400
                         disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {upsert.isPending ? 'Saving…' : editingSku ? 'Update SKU' : 'Create SKU'}
            </button>
            {upsert.isError && (
              <span className="text-xs text-rose-400">
                {(upsert.error as Error)?.message ?? 'Failed to save SKU'}
              </span>
            )}
          </div>
        </form>
      )}

      {/* ── SKU List (or empty / loading / error state) ─────────────── */}
      {isLoading && (
        <p className="text-sm text-neutral-500 animate-pulse py-2">Loading SKU catalog…</p>
      )}

      {isError && (
        <p className="text-xs text-rose-400 py-2">
          {(error as Error)?.message ?? 'Failed to load SKUs'}
        </p>
      )}

      {listReady && items.length === 0 && !showForm ? (
        <div className="py-6 text-center">
          <p className="text-sm text-neutral-500 mb-3">
            No SKU definitions yet. Create one to start provisioning devices.
          </p>
          <button
            type="button"
            onClick={openNew}
            className="pac-glass-btn px-4 py-2 rounded text-sm font-medium border border-neutral-700
                       text-neutral-300 hover:border-emerald-500 hover:text-emerald-400 transition-colors"
          >
            + Create First SKU
          </button>
        </div>
      ) : items.length > 0 && (
        <div className="divide-y divide-white/5">
          {items.map((s) => (
            <div
              key={s.sku}
              className="flex items-center gap-4 py-3 first:pt-0 last:pb-0"
            >
              {/* Active badge */}
              <span
                className={`inline-block w-2 h-2 rounded-full shrink-0 ${
                  s.active ? 'bg-emerald-500' : 'bg-neutral-600'
                }`}
                title={s.active ? 'Active' : 'Inactive'}
              />

              {/* Identity */}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-neutral-200 truncate font-medium">
                  {s.label}
                </p>
                <p className="text-[10px] text-neutral-500 font-mono truncate">
                  {s.sku}
                  <span className="ml-2 text-neutral-600">v{s.version}</span>
                  <span className="ml-2 px-1.5 py-0.5 rounded bg-white/5 text-[9px] uppercase">
                    {s.category}
                  </span>
                  <span className={`ml-1 px-1.5 py-0.5 rounded text-[9px] uppercase ${
                    (s.sku_type ?? 'assembly') === 'component'
                      ? 'bg-blue-500/10 text-blue-400'
                      : 'bg-purple-500/10 text-purple-400'
                  }`}>
                    {s.sku_type ?? 'assembly'}
                  </span>
                  <span className="ml-1 px-1.5 py-0.5 rounded bg-white/5 text-[9px] text-neutral-500">
                    T{s.tier ?? 0}
                  </span>
                  {s.parent_sku && (
                    <span className="ml-1 text-[9px] text-neutral-600">
                      {'\u2190'} {s.parent_sku}
                    </span>
                  )}
                </p>
              </div>

              {/* IoT summary */}
              <div className="hidden sm:flex items-center gap-3 text-[10px] text-neutral-500 shrink-0">
                {s.publish_period_ms != null && (
                  <span title="Publish interval">{(s.publish_period_ms / 1000).toFixed(0)}s pub</span>
                )}
                {s.sensor_interval_ms != null && (
                  <span title="Sensor read interval">{(s.sensor_interval_ms / 1000).toFixed(0)}s sens</span>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => openEdit(s)}
                  className="text-[10px] text-neutral-500 hover:text-purple-400 transition-colors"
                  title="Edit"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => toggleActive(s)}
                  disabled={deactivate.isPending || reactivate.isPending}
                  className={`text-[10px] transition-colors disabled:opacity-40 ${
                    s.active
                      ? 'text-neutral-500 hover:text-rose-400'
                      : 'text-neutral-500 hover:text-emerald-400'
                  }`}
                  title={s.active ? 'Deactivate' : 'Reactivate'}
                >
                  {s.active ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
